// This class manages the tiptap editor and the hocuspocus provider

import Inserted from "@sudowrite/extension-inserted";
import { Editor as TipTapEditor } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Collaboration from "@tiptap/extension-collaboration";
import { Document as TipTapDocument } from "@tiptap/extension-document";
import Hardbreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import History from "@tiptap/extension-history";
import Image from "@tiptap/extension-image";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Strike from "@tiptap/extension-strike";
import Text from "@tiptap/extension-text";
import Typography from "@tiptap/extension-typography";
import Underline from "@tiptap/extension-underline";
import Annotation from "../../../src/shared/annotation";
import { ThemeAwareHighlight } from "./tipTapExtensions/themeAwareHighlight";
import { createTaskListExtensions } from "./tipTapExtensions/taskListExtensions";

import { HocuspocusProvider } from "@hocuspocus/provider";
import { TiptapTransformer } from "@hocuspocus/transformer";
import * as Sentry from "@sentry/react";
import { reportContentError } from "./contentErrorReporter";
import { pushClientSnapshot as postClientSnapshot } from "../components/TiptapDocumentSB/documentSnapshots";
import { Extension } from "@tiptap/core";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import TrackChangeExtension from "../../../src/shared/trackChanges";

import Placeholder from "@tiptap/extension-placeholder";
import { Plugin } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { GrammarExtension } from "./tipTapExtensions/grammar";
import { PersistedSelectionExtension } from "./tipTapExtensions/persistedSelection";
import { countWords, keepWordsFromEnd } from "../Utils";
import { PasteFromTextBoxExtension } from "./tipTapExtensions/pasteFromTextBox";
import { isExplicitUrl, PasteUrlAsLink } from "./tipTapExtensions/pasteUrlAsLink";
import { SplitParagraphTrimsBreaks } from "./tipTapExtensions/splitParagraphTrimsBreaks";
import SearchAndReplace from "./tipTapExtensions/searchAndReplace";
import { registerDevTools } from "./devTools";
import { serializeSliceToMarkdown } from "./clipboardTextSerializer";

function applyDecoration(state, view, fromPos, toPos) {
  console.log("applyDecoration", fromPos, toPos);
  let transaction = state.tr.setMeta("highlightDecoration", { fromPos: fromPos, toPos: toPos });
  // transaction = transaction.step(new ReplaceStep(fromPos, fromPos, state.tr.doc.slice(fromPos, fromPos)));
  view.dispatch(transaction);
}

// coordsAtPos throws when ProseMirror is asked about a position outside the
// current document. Resolve the document's valid upper bound before touching
// the DOM so stale local highlight state can be dropped quietly.
function getDocMaxPosition(view) {
  const doc = view?.state?.doc;

  if (Number.isFinite(doc?.content?.size)) {
    return doc.content.size;
  }

  // nodeSize includes the document's opening and closing tokens; subtract them
  // to get the same content-coordinate max used by coordsAtPos.
  if (Number.isFinite(doc?.nodeSize)) {
    return Math.max(doc.nodeSize - 2, 0);
  }

  return null;
}

function isValidEditorPosition(pos, maxPosition) {
  return Number.isFinite(pos) && pos >= 0 && (maxPosition === null || pos <= maxPosition);
}

function isOutOfRangeCoordsError(error) {
  // Keep this narrow so unexpected coordinate failures still go to Sentry.
  return /position .* out of range/i.test(error?.message || "");
}

// Tiptap extension that wraps the prose mirror highlight decoration plugin
const highlightExtension = Extension.create({
  addProseMirrorPlugins() {
    return [highlightDecoration];
  },
});

const highlightDecoration = new Plugin({
  state: {
    init() {
      // No decorations set by default
      return DecorationSet.empty;
    },
    apply(tr, value) {
      const highlightMeta = tr.getMeta("highlightDecoration");
      if (highlightMeta?.clear) {
        return DecorationSet.empty;
      }
      if (highlightMeta) {
        const { fromPos, toPos, className } = highlightMeta;
        return DecorationSet.create(tr.doc, [
          Decoration.inline(fromPos, toPos, {
            class: className ? `sudo-agent-edit-highlight ${className}` : "sudo-agent-edit-highlight",
          }),
        ]);
      } else {
        // map "other" changes so our decoration "stays put"
        // (e.g. user is typing so decoration's pos must change)
        return value.map(tr.mapping, tr.doc);
      }
    },
  },
  props: {
    decorations(state) {
      return highlightDecoration.getState(state);
    },
  },
});

const DisableMetaEnter = Extension.create({
  addKeyboardShortcuts() {
    return {
      "Meta-Enter": () => true,
    };
  },
});

/**
 * @typedef {Object} EditorOptions
 * @property {string} documentId document id
 * @property {string} user firebase user
 * @property {function} onSynced called when the provider is synced
 * @property {function} onConnect called when the provider is connected
 */
/**
 * SW-9064: Returns the currently active TipTap editor (exposed via
 * `window.tiptap` by TiptapDocumentSB on mount). Returns null when no
 * editor is mounted (e.g. user is on the dashboard).
 */
export function getCurrentEditor() {
  return typeof window !== "undefined" ? window.tiptap || null : null;
}

export default class Editor {
  constructor({ documentId, user, onSynced, onConnect, showError, setSaved, editorHandlers, canWriteChecklists = false }) {
    this.canWriteChecklists = canWriteChecklists;
    this.ydoc = new Y.Doc();
    this.ydocLocalStorage = null;
    try {
      this.ydocLocalStorage = new IndexeddbPersistence(documentId, this.ydoc);
      this.ydocLocalStorage.on("synced", () => {
        // console.log("loaded content from local storage");
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "indexeddb_persistence_init", documentId },
      });
    }
    this.onSynced = onSynced;
    this.onConnect = onConnect;
    this.user = user;
    this.documentId = documentId;
    this.disconnectTimeout = null;
    this.inited = false;
    this.showError = showError;
    this.setSaved = setSaved;
    this.annotationConfig = editorHandlers.annotations;
    this._visibilityHandler = null;
    this._destroyed = false;
    this._visibilityTimeout = null;
    this._authRetryCount = 0;
    this._providerConnected = false;
    // Client-pushed snapshot fallback: covers stale-tab and offline-editing
    // failure modes. The disconnect/auth-recovery paths intentionally keep the
    // editor editable while IndexedDB preserves local Yjs changes, so snapshots
    // are POSTed over plain HTTP (independent of the WS) as a second route to
    // server-side recovery. Server-side identity dedup
    // (processSynchronousSnapshot) collapses identical-content duplicates, so
    // we fire liberally and let the server filter.
    this._snapshotBeforeUnloadHandler = null;
    this._snapshotVisibilityHandler = null;
    this._cachedIdToken = null;
    this._cachedIdTokenInterval = null;
    // Snapshot-push telemetry surfaced to dev tools (sw.snapshot.status).
    this._lastSnapshotPushAt = null;
    this._lastSnapshotPushReason = null;
    this._lastSnapshotPushBytes = null;
  }

  // Initializes the editor and provider
  async init() {
    // Wait for IndexedDB to load local state BEFORE connecting to the server.
    // This ensures local changes (which may be more recent than the server state)
    // are in the Ydoc before the Hocuspocus sync begins, preventing the server's
    // stale state from being treated as the base document.
    // Timeout after 5 seconds — if IndexedDB is corrupted or blocked, proceed
    // without local state rather than hanging the editor forever.
    if (this.ydocLocalStorage) {
      await new Promise((resolve) => {
        if (this.ydocLocalStorage.synced) {
          resolve();
          return;
        }

        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };

        const onSynced = () => {
          clearTimeout(timeout);
          this.ydocLocalStorage?.off?.("synced", onSynced);
          finish();
        };

        const timeout = setTimeout(() => {
          this.ydocLocalStorage?.off?.("synced", onSynced);
          Sentry.captureMessage("IndexedDB sync timed out after 5 seconds, proceeding without local state", {
            tags: { documentId: this.documentId },
          });
          finish();
        }, 5000);

        this.ydocLocalStorage.once("synced", onSynced);
      });
    }

    // If the editor was destroyed while waiting for IndexedDB, bail out
    if (this._destroyed) return;

    // A restored IndexedDB manuscript is already usable local document state,
    // even if WebSocket authentication/sync never succeeds. Check before TipTap
    // inserts its default empty paragraph: an empty cache is not a loaded document.
    this.localDocumentLoaded = this.ydocLocalStorage?.synced === true
      && this.ydoc.getXmlFragment("default").length > 0;

    const token = async () => {
      // console.log("[hocuspocus] getting firebase token");
      return await this.user.getIdToken(false);
    }; // gets the id token from firebase

    const hostInstance = this;
    const params = {
      url: process.env.REACT_APP_WEBSOCKET_URL,
      name: this.documentId,
      document: this.ydoc,
      token,
      onOpen() {
        clearTimeout(hostInstance.disconnectTimeout);
        hostInstance._providerConnected = true;
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onOpen — connection established`,
          data: { documentId: hostInstance.documentId },
        });
        // Safety net: restore editability in case any code path locked the editor.
        if (hostInstance.tiptap) {
          hostInstance.tiptap.setEditable(true);
        }
      },
      onAuthenticated() {
        hostInstance._authRetryCount = 0;
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onAuthenticated`,
          data: { documentId: hostInstance.documentId },
        });
      },
      onAuthenticationFailed({ reason }) {
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onAuthenticationFailed`,
          data: { documentId: hostInstance.documentId, reason },
        });

        // Attempt recovery by force-refreshing the Firebase token and reconnecting.
        // This handles the common case where a cached token expired during sleep/wake/disconnect.
        // The editor stays editable — IndexedDB preserves changes locally.
        hostInstance._authRetryCount++;

        if (hostInstance._authRetryCount <= 2) {
          Sentry.addBreadcrumb({
            message: `[hocuspocus] Attempting auth recovery (attempt ${hostInstance._authRetryCount})`,
            data: { documentId: hostInstance.documentId, reason },
          });
          hostInstance.user
            .getIdToken(true)
            .then((freshToken) => {
              if (hostInstance._destroyed) return;
              hostInstance._cachedIdToken = freshToken;
              clearTimeout(hostInstance.disconnectTimeout);
              hostInstance.provider.disconnect();
              hostInstance.provider.connect();
            })
            .catch((tokenError) => {
              Sentry.captureMessage("HocusPocus Auth Failed (token refresh failed): " + reason, {
                tags: { documentId: hostInstance.documentId },
              });
              hostInstance.showError("Your changes are saving locally but not to the server.");
            });
        } else {
          Sentry.captureMessage("HocusPocus Auth Failed (retries exhausted): " + reason, {
            tags: { documentId: hostInstance.documentId },
          });
          hostInstance.showError("Your changes are saving locally but not to the server.");
        }
      },
      onStatus: ({ status }) => {
        // …
      },
      onMessage: ({ event, message }) => {
        // …
      },
      onOutgoingMessage: ({ message }) => {
        // …
      },
      onClose: ({ event }) => {
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onClose`,
          data: { documentId: hostInstance.documentId, code: event?.code, reason: event?.reason },
        });
      },
      onDisconnect: ({ event }) => {
        hostInstance._providerConnected = false;
        Sentry.addBreadcrumb({
          message: `[hocuspocus] onDisconnect`,
          data: { documentId: hostInstance.documentId, code: event?.code, reason: event?.reason },
        });
        hostInstance.disconnectTimeout = setTimeout(() => {
          Sentry.captureMessage("HocusPocus Unable to reconnect after 30 seconds.", {
            tags: {
              documentId: hostInstance.documentId,
            },
          });
          // Don't lock the editor or show an error toast — IndexedDB preserves
          // changes locally and the provider will continue retrying. The saved
          // indicator already reflects the unsynced state.
        }, 30000);
      },
      onDestroy() {
        // …
        // console.log("[hocuspocus] onDestroy");
        clearTimeout(hostInstance.disconnectTimeout);
      },
      onAwarenessUpdate: ({ added, updated, removed }) => {
        // …
      },
      onAwarenessChange: ({ states }) => {
        // …
      },
      onStateless: ({ payload }) => {
        // ...
        // the provider can also send a custom message to the server: provider.sendStateless('any string payload')
      },
    };

    if (this.onConnect) {
      params.onConnect = this.onConnect;
    }
    if (this.onSynced) {
      params.onSynced = this.onSynced;
    }

    // Construct the HocuspocusProvider instance
    this.provider = new HocuspocusProvider(params);

    // Expose debug/test helpers in dev (see devTools.js for available commands)
    if (process.env.NODE_ENV === "development") {
      window.__sudoEditor = this;
      registerDevTools(this);
    }

    // Keep the saved indicator based on sync + connection state.
    // Three states: "saved" (synced to server), "saving" (has unsynced changes
    // and connected), "saved-locally" (disconnected — changes are safe in IndexedDB).
    this.savedIndicatorInterval = setInterval(() => {
      // Check multiple signals — any one being offline triggers "Saved Locally":
      // - _providerConnected: set by onOpen/onDisconnect callbacks (most reliable)
      // - navigator.onLine: flips immediately when browser/OS goes offline
      const connected = this._providerConnected && navigator.onLine;
      const hasUnsynced = this.provider.hasUnsyncedChanges;

      if (!connected) {
        if (this.ydocLocalStorage) {
          // Disconnected with IndexedDB persistence: the user can keep writing
          // and local Yjs state will sync on reconnect.
          this.setSaved?.("saved-locally");
        } else {
          this.setSaved?.(hasUnsynced ? "saving" : "saved");
        }
      } else if (hasUnsynced) {
        this.setSaved?.("saving");
      } else {
        this.setSaved?.("saved");
      }
    }, 1000);

    // When the tab becomes visible after Chrome freezes it, automatically
    // recover the websocket connection. Chrome aggressively freezes background
    // tabs, silently killing websockets. We fix this transparently — the user
    // should never need to manually intervene.
    this._reconnectAttempts = 0;
    this._visibilityHandler = () => {
      // Cancel any pending visibility check to prevent stacking from rapid tab
      // switches. If the tab is hidden again before the follow-up fires, don't
      // diagnose a zombie socket from a throttled background tab.
      clearTimeout(this._visibilityTimeout);

      if (document.visibilityState === "visible" && this.provider) {

        // Snapshot lastMessageReceived BEFORE we trigger any sync attempts so
        // we can tell after the timeout whether the WS is actually responsive.
        // A "zombie" WS reports status=connected but never delivers messages
        // (Chrome can park a backgrounded tab's socket while the server-side
        // broadcasts pile up unread).
        const lastMessageBefore = this.provider.lastMessageReceived || 0;
        let forceSyncSent = false;

        if (this.provider.status !== "connected") {
          Sentry.addBreadcrumb({
            message: `Tab became visible, provider status: ${this.provider.status}, reconnecting`,
            data: { documentId: this.documentId },
          });
          this._reconnectAttempts = 0;
          this.provider.connect();
        } else {
          // Provider thinks it's connected, but background-tab throttling may
          // have left it stale (missed inbound broadcasts while frozen). Send
          // a fresh state vector so the server replies with anything we're
          // missing. Cheap — one small message — and pulls remote updates
          // within hundreds of ms instead of waiting for the 5s fallback or
          // a natural WS keepalive timeout.
          this.provider.forceSync();
          forceSyncSent = true;
        }

        // Verify the sync took effect after a delay. Two independent failure
        // modes can need a hard reconnect:
        //   (a) Local unsynced changes still pending — status reports connected
        //       but our writes aren't getting through.
        //   (b) Zombie WS — status reports connected but lastMessageReceived
        //       hasn't advanced after a forceSync(). The server always answers
        //       SyncStep1 with at least an empty SyncStep2; absence of any
        //       reply after 5s means the socket is silently dead. We only run
        //       this branch when forceSync() was sent; a plain connect() can
        //       succeed without immediate inbound traffic.
        // If either fires, do a full disconnect/reconnect cycle.
        this._visibilityTimeout = setTimeout(() => {
          if (!this.provider) return;
          const lastMessageAfter = this.provider.lastMessageReceived || 0;
          const localUnsynced = this.provider.hasUnsyncedChanges && this.provider.status === "connected";
          const wsIsZombie = forceSyncSent && this.provider.status === "connected" && lastMessageBefore > 0 && lastMessageAfter === lastMessageBefore;
          if (localUnsynced || wsIsZombie) {
            this._reconnectAttempts++;
            if (this._reconnectAttempts <= 3) {
              Sentry.addBreadcrumb({
                message: `Sync stale after tab visible, reconnect attempt ${this._reconnectAttempts}`,
                data: { documentId: this.documentId, wsIsZombie, localUnsynced },
              });
              clearTimeout(this.disconnectTimeout);
              this.provider.disconnect();
              this.provider.connect();
            } else {
              // Automatic recovery failed — warn the user
              Sentry.captureMessage("HocusPocus sync recovery failed after tab visible", {
                tags: { documentId: this.documentId },
              });
              this.showError?.("Your changes may not be saving. Please check your connection.");
            }
          } else {
            // Sync recovered successfully
            this._reconnectAttempts = 0;
          }
        }, 5000);
      }
    };
    document.addEventListener("visibilitychange", this._visibilityHandler);

    this.tiptap = new TipTapEditor({
      enableContentCheck: true,
      onContentError: ({ error }) => {
        reportContentError(error, this.documentId);
      },
      // Disable the browser's native spellcheck on the editor surface; our
      // LT-backed spell + grammar check (toggled from the toolbar) is the
      // source of truth.
      editorProps: {
        attributes: {
          spellcheck: "false",
        },
        // text/plain clipboard flavor: markdown-ish (list markers, headings,
        // one blank line between paragraphs, spacer paragraphs skipped).
        // text/html keeps carrying real markup for rich targets.
        clipboardTextSerializer: (slice, view) => serializeSliceToMarkdown(slice, view.state.selection.$from),
      },
      extensions: [
        Collaboration.configure({
          document: this.ydoc,
        }),
        TipTapDocument,
        Paragraph,
        Typography,
        Text,
        Bold,
        Italic,
        Heading,
        Underline,
        Hardbreak,
        Strike,
        ThemeAwareHighlight.configure({
          multicolor: true,
        }),
        BulletList,
        OrderedList,
        ListItem,
        ...createTaskListExtensions({ canWrite: this.canWriteChecklists }),
        Inserted,
        Link.configure({
          // Autolink only fires for explicit URLs (http(s):// or www.) so an
          // accidental missing space after a period ("word.word") never
          // linkifies. PasteUrlAsLink covers URLs pasted at a caret.
          autolink: true,
          shouldAutoLink: isExplicitUrl,
          defaultProtocol: "https",
          openOnClick: false,
        }),
        PasteUrlAsLink,
        Image.configure({
          inline: true,
        }),
        History,
        DisableMetaEnter,
        SplitParagraphTrimsBreaks,
        TrackChangeExtension.configure({
          enabled: false,
          dataOpUserId: "", // set the op userId
          dataOpUserNickname: "", // set the op user nickname
          onStatusChange(status) {
            // myTrackChangeEnabled = status
          },
          onStrayMarksCleaned: ({ count, staleSessionIds, currentSessionId, error }) => {
            Sentry.captureMessage(
              error
                ? "Quick Edit track-change reAdd step failed"
                : "Quick Edit cleaned stray tracked-change marks",
              {
                level: error ? "error" : "warning",
                tags: {
                  feature: "trackChanges_strayCleanup",
                  documentId: this.documentId,
                },
                extra: {
                  count,
                  staleSessionIds,
                  currentSessionId,
                  error,
                },
              },
            );
          },
        }),
        Placeholder.configure({
          placeholder: "Type here...",
        }),
        highlightExtension,
        PersistedSelectionExtension,
        SearchAndReplace.configure(),
        GrammarExtension.configure(),
        PasteFromTextBoxExtension.configure(),
        Annotation.configure({ ...this.annotationConfig, preserveCommentsOnUndoRedo: true }),
      ],
      onUpdate: () => {
        this.setSaved?.(!this.provider.hasUnsyncedChanges);
      },
    });

    // Client-pushed snapshot fallback. The WS sync covers the happy path; these
    // triggers cover cases where the provider is disconnected or stale while
    // the editor remains editable and local changes continue landing in
    // IndexedDB (background tab WS idle-out, Firebase token expiry mid-session,
    // network changes, etc.). Snapshots POST over plain HTTP using the existing
    // /api/document/snapshots/create-document-snapshot endpoint, which calls
    // processSynchronousSnapshot — that already dedups identical-content
    // snapshots server-side. We fire liberally and let the server filter.
    this._refreshCachedIdToken();
    this._cachedIdTokenInterval = setInterval(() => {
      this._refreshCachedIdToken();
    }, 30 * 60 * 1000); // refresh well ahead of the 1-hour Firebase token expiry

    this._snapshotVisibilityHandler = () => {
      if (document.visibilityState === "hidden") {
        // Visibility-hidden: page is still alive (just backgrounded), so no
        // keepalive required — and skipping it lets large docs through.
        this.pushClientSnapshot("visibility-hidden", { keepalive: false });
      }
    };
    document.addEventListener("visibilitychange", this._snapshotVisibilityHandler);

    this._snapshotBeforeUnloadHandler = () => {
      // beforeunload: page is actually unloading — keepalive is required for
      // the request to complete. Large docs (>~64KB) may fail here; the
      // visibility-hidden trigger above is the primary safety net for those,
      // since most users background the tab before closing it.
      this.pushClientSnapshot("beforeunload", { keepalive: true });
    };
    window.addEventListener("beforeunload", this._snapshotBeforeUnloadHandler);

    this.inited = true;
  }

  async _refreshCachedIdToken() {
    if (!this.user || this._destroyed) return;
    try {
      const token = await this.user.getIdToken(false);
      // Re-check destroyed after the await: destroy() could have run while we
      // were awaiting the token. Don't mutate a torn-down editor instance.
      if (this._destroyed) return;
      this._cachedIdToken = token;
    } catch (_err) {
      // Token refresh failures are tolerated; the next interval will retry.
      // pushClientSnapshot bails out cleanly if the cached token is null.
    }
  }

  pushClientSnapshot(reason, { keepalive = false } = {}) {
    // Preconditions: editor must be alive and the cached id token must be
    // populated. Token caching is kicked off at init and refreshed every
    // 30 min; pushes that fire before the first refresh resolves (rare) or
    // after destroy() are silently dropped — the next trigger will retry.
    if (this._destroyed || !this.ydoc || !this.documentId) return;
    if (!this._cachedIdToken) return;

    let snapshotContent;
    try {
      snapshotContent = TiptapTransformer.fromYdoc(this.ydoc);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { documentId: this.documentId, operation: "client_snapshot_encode", reason },
      });
      return;
    }

    this._lastSnapshotPushAt = Date.now();
    this._lastSnapshotPushReason = reason;
    this._lastSnapshotPushBytes = JSON.stringify(snapshotContent).length;

    postClientSnapshot({
      documentId: this.documentId,
      snapshotContent,
      idToken: this._cachedIdToken,
      reason,
      keepalive,
    });
  }

  clearAllHighlights() {
    applyDecoration(this.tiptap.state, this.tiptap.view, 0, 0);
    this.highlightRange = null;
    this.highlightSticky = false;
  }

  isHighlightSticky() {
    return !!this.highlightSticky;
  }

  getHighlightedText() {
    // Prefer the explicitly saved highlight range over decoration-based lookup.
    // Decorations get remapped by track-change transactions and can shift/widen.
    if (this.highlightRange) {
      const { from, to } = this.highlightRange;
      const docSize = this.tiptap.view.state.doc.content.size;
      if (from >= 0 && to <= docSize && from <= to) {
        const text = this.tiptap.view.state.doc.textBetween(from, to);
        return { text, from, to };
      }
      this.highlightRange = null;
    }

    const highlights = this.findHighlights();

    // if nothing is highlighted...
    if (!highlights) {
      // return an empty string and the current cursor position
      const currentPos = this.lastCursorPosition || 0;

      return {
        text: "",
        from: currentPos,
        to: currentPos,
      };
    }

    const highlightRange = highlights[0];

    // const firstRange = ranges[0];
    const text = this.tiptap.view.state.doc.textBetween(highlightRange.from, highlightRange.to);
    return {
      text,
      from: highlightRange.from,
      to: highlightRange.to,
    };
  }

  getPrecedingText(maxWords = 2000) {
    const currentPos = this.lastCursorPosition || 0;
    const allText = this.tiptap.view.state.doc.textBetween(0, currentPos);

    if (countWords(allText) <= maxWords) {
      return allText;
    }

    return keepWordsFromEnd(maxWords, allText);
  }

  saveSelection() {
    this.savedSelection = this.tiptap.state.selection;
  }

  restoreSelection() {
    this.tiptap.commands.focus(0);
    if (this.savedSelection) {
      this.tiptap.commands.setTextSelection({ from: this.savedSelection.from, to: this.savedSelection.to });
    }
  }

  setLastCursorPosition(pos) {
    this.lastCursorPosition = pos ?? this.tiptap.state.selection.head;
  }

  highlight(from, to, save = false, options = {}) {
    const { clearSelection = true, className, sticky = false } = options;
    const { state, view } = this.tiptap;
    let transaction = state.tr.setMeta("highlightDecoration", { fromPos: from, toPos: to, className });
    view.dispatch(transaction);

    // Sticky highlights survive document clicks (e.g. the Quick Edit success
    // highlight stays until the user accepts/rejects). Non-sticky highlights
    // (e.g. agent edit highlights) are dismissed on the next interaction.
    this.highlightSticky = sticky;

    if (save) {
      this.highlightRange = {
        from: from,
        to: to,
      };
    }

    if (clearSelection) {
      this.clearSelection();
    }
  }

  clearHighlight() {
    const { state, view } = this.tiptap;
    const transaction = state.tr.setMeta("highlightDecoration", { clear: true });
    view.dispatch(transaction);
    this.highlightSticky = false;
  }

  findHighlights() {
    const { state } = this.tiptap; // PM EditorState object
    const decorationSet = highlightDecoration.getState(state);

    const decorations = decorationSet.find();
    const highlights = decorations
      .filter((decoration) => (decoration.type.attrs.class || "").split(" ").includes("sudo-agent-edit-highlight"))
      .map((decoration) => {
        return { from: decoration.from, to: decoration.to };
      });
    if (highlights.length > 0) {
      return highlights;
    } else {
      return null;
    }
  }

  getHighlightCoords() {
    const highlights = this.findHighlights();
    let from, to;
    const usingHighlightRange = Boolean(highlights?.length);

    if (!highlights) {
      // use last cursor position if nothing is highlighted
      from = this.lastCursorPosition || 0;
      to = this.lastCursorPosition || 0;
    } else {
      // get the range of the first highlight
      from = highlights[0].from;
      to = highlights[0].to;
    }

    const { view } = this.tiptap;
    const maxPosition = getDocMaxPosition(view);
    const staleRange =
      from > to ||
      !isValidEditorPosition(from, maxPosition) ||
      !isValidEditorPosition(to, maxPosition);

    if (staleRange) {
      // Collaborative document updates can leave either the decoration range or
      // the remembered cursor position pointing beyond the current doc. Clear
      // whichever local source supplied the stale coordinates and skip Sentry
      // because there is no actionable editor failure to report.
      if (usingHighlightRange) {
        this.clearHighlight();
      } else {
        this.lastCursorPosition = null;
      }

      return null;
    }

    // Get the coordinates for the start and end of the highlight
    let startCoords;
    let endCoords;

    try {
      startCoords = view.coordsAtPos(from);
      endCoords = view.coordsAtPos(to);
    } catch (e) {
      if (isOutOfRangeCoordsError(e)) {
        // The document can still change between the preflight bounds check and
        // coordsAtPos. Treat that race the same as any other stale local range.
        if (usingHighlightRange) {
          this.clearHighlight();
        } else {
          this.lastCursorPosition = null;
        }

        return null;
      }

      const err = new Error(`Editor cannot get coordinates for highlights: ${e.message}`);
      Sentry.captureException(err, {
        tags: {
          documentId: this.documentId,
          user: this.user,
          highlights,
          from,
          to,
        },
      });
      return null;
    }

    // bounding pixel positions of the highlight, in the #editor host's content
    // coordinate space (overlays are absolutely positioned inside the editor,
    // so viewport coords from coordsAtPos are translated by the host rect).
    const host = view.dom?.closest?.("#editor");
    const hostRect = host?.getBoundingClientRect();
    const offsetLeft = hostRect?.left ?? 0;
    const offsetTop = hostRect?.top ?? 0;

    const boundingRect = {
      left: startCoords.left - offsetLeft,
      top: startCoords.top - offsetTop,
      right: endCoords.right - offsetLeft,
      bottom: endCoords.bottom - offsetTop,
    };

    return boundingRect;
  }

  getPosFromNode(DOMnode) {
    const { view } = this.tiptap; // PM EditorView object
    const from = view.posAtDOM(DOMnode, 0); // Offset 0 for the start of the node
    // const resolvedPos = view.state.doc.resolve(from); // Resolve the position to a node
    // const to = resolvedPos.end(); // Get the end position of the node
    const to = from + DOMnode.textContent.length;
    return { from, to };
  }

  clearSelection() {
    this.tiptap.commands.setTextSelection(0); // from: 0, to: 0
  }

  destroy() {
    // Fire one last client-pushed snapshot BEFORE any teardown. Catches the
    // SPA-navigation case where the user leaves the doc without triggering
    // beforeunload — e.g., clicking another doc in the sidebar, where React
    // unmounts the editor but the page itself doesn't unload. The page stays
    // alive during SPA navigation, so keepalive is unnecessary here — and
    // would actually cap us at the ~64KB keepalive body limit, silently
    // failing on large docs. Must run before `_destroyed` is set, otherwise
    // pushClientSnapshot would short-circuit.
    try {
      this.pushClientSnapshot("destroy", { keepalive: false });
    } catch (_err) {
      // never block destroy on snapshot push failures
    }

    // Disconnect the WebSocket provider before tearing down the editor.
    // We intentionally do NOT mutate document content during destroy:
    // rejecting tracked changes here can delete insertion-marked (purple)
    // text from local Yjs state during normal document navigation.
    try {
      this.provider?.disconnect?.();
    } catch (_err) {
      // never block destroy on disconnect failures
    }

    this._destroyed = true;

    if (this._visibilityHandler) {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
      this._visibilityHandler = null;
    }
    clearTimeout(this._visibilityTimeout);

    // Stop the client-pushed snapshot triggers.
    if (this._cachedIdTokenInterval) {
      clearInterval(this._cachedIdTokenInterval);
      this._cachedIdTokenInterval = null;
    }
    if (this._snapshotVisibilityHandler) {
      document.removeEventListener("visibilitychange", this._snapshotVisibilityHandler);
      this._snapshotVisibilityHandler = null;
    }
    if (this._snapshotBeforeUnloadHandler) {
      window.removeEventListener("beforeunload", this._snapshotBeforeUnloadHandler);
      this._snapshotBeforeUnloadHandler = null;
    }

    let tiptap = false;
    let provider = false;
    // console.log("TipTap Editor getting wrecked");
    if (this.tiptap) {
      this.tiptap.destroy();
      //  console.log("TipTap Instance destroyed");
      tiptap = true;
    }
    if (this.provider) {
      this.provider.destroy();
      // console.log("HocusPocus Provider destroyed");
      provider = true;
    }
    if (this.ydoc) {
      this.ydoc.destroy();
      // console.log("YDoc destroyed");
    }
    // console.log("TipTap Editor destroyed");
    Sentry.addBreadcrumb({
      message: `TipTap Editor destroyed: tiptap: ${tiptap}, provider: ${provider}`,
    });
    clearInterval(this.savedIndicatorInterval);
  }
}
